/**
 * Logic behind the one storage unit page (`StorageUnitView`): its URLs, which
 * side-panel tabs a user is offered, who may open Settings, the header's
 * summary line, and which grid the map draws for a selection. Pure so it is covered: this repo's vitest only
 * collects `src/**\/*.test.ts`.
 */

import { resolveKeptAt } from '@/lib/keptAt'
import { inheritedZone } from '@/lib/storageSettingsForm'
import { isOccupiable } from '@/lib/storageTree'
import {
  STORAGE_NODE_KIND_LABELS,
  type ApiStorageNode,
  type StorageCapacity,
  type StorageCondition,
  type StorageNodeKind,
} from '@/types/storage'

/** Which tab the page was opened from. Both render the same page. */
export type UnitSurface = 'inventory' | 'equipment'

/**
 * The side-panel tabs. Contents (what is in the selection) is Inventory's;
 * Selected (what the selection is, and blocking a slot) is Equipment's.
 * Settings is not a tab — it is a modal, opened by `?panel=settings`.
 */
export type PanelTab = 'contents' | 'selected' | 'history'

/** What `?panel=` may carry: a tab, or `settings` to open the Settings modal. */
export type PanelParam = PanelTab | 'settings'

/**
 * The unit page's URL. `node` and `panel` live in the query so a link or a
 * reload lands on the same selection and tab.
 */
export function unitPageHref(
  surface: UnitSurface,
  unitId: string,
  opts: { node?: string | null; panel?: PanelParam | null } = {},
): string {
  const params = new URLSearchParams()
  if (opts.node) params.set('node', opts.node)
  if (opts.panel) params.set('panel', opts.panel)
  const query = params.toString()
  return `/${surface}/storage/${unitId}${query ? `?${query}` : ''}`
}

/**
 * Where the breadcrumb's "Storage" goes: the list on the surface's own tab, or
 * Products when that is where the user came from (it lists units too). Any
 * other origin is not followed — naming the wrong screen is worse than naming
 * the surface's own.
 */
export function storageListHref(surface: UnitSurface, from?: string | null): string {
  if (from && /^\/products(\?|$)/.test(from)) return from
  return `/${surface}?tab=storage`
}

/**
 * What a storage screen says instead of an error when the user lacks access to
 * the inventory routes it reads — the unit page and the list say the same.
 */
export const NO_INVENTORY_ACCESS =
  'Seeing inside a storage unit needs access to Inventory. Ask an administrator for it.'

/** The server refused the request (403) — `storageApi` carries the status on its errors. */
export function isForbidden(error: unknown): boolean {
  return error instanceof Error && (error as Error & { status?: number }).status === 403
}

/**
 * The side-panel tabs this user may open.
 *
 * Equipment is the configuration view and never shows what is stored, so it
 * gets Selected instead of Contents, whatever the user may do. Contents and
 * History both read inventory routes, so without the inventory feature or
 * `inventory.view` they would only ever 403; Selected is drawn from the tree
 * already loaded.
 */
export function panelTabs({
  surface,
  inventoryParts,
}: {
  surface: UnitSurface
  inventoryParts: boolean
}): PanelTab[] {
  const tabs: PanelTab[] = []
  if (surface === 'equipment') tabs.push('selected')
  else if (inventoryParts) tabs.push('contents')
  if (inventoryParts) tabs.push('history')
  return tabs
}

/**
 * The `?panel=` value if it is offered, else the first offered tab. `slot` is
 * what links made before the Contents tab was named say.
 */
export function resolvePanelTab(requested: string | null, offered: PanelTab[]): PanelTab | null {
  const wanted = requested === 'slot' ? 'contents' : requested
  return offered.find((tab) => tab === wanted) ?? offered[0] ?? null
}

/**
 * Whether the Settings modal has anything in it for this user: editing the
 * node (`inventory.configure`), or replacing the unit (`canReplace` —
 * `storage.replace` on the unit node, which has equipment). Every entry point
 * to it — the header button, Edit settings, `?panel=settings` — asks this.
 */
export function canOpenSettings({ canConfigure, canReplace }: { canConfigure: boolean; canReplace: boolean }): boolean {
  return canConfigure || canReplace
}

/**
 * Which Settings entry points to show. The header's "⚙ Settings" always means
 * the unit; "⚙ Edit settings" on the Selected card means the selection. Each
 * is shown only when the modal would hold something this user may do for its
 * node — Replace counts only for the unit itself.
 */
export function settingsEntryPoints({
  canConfigure,
  canReplaceUnit,
  selectedIsUnit,
}: {
  canConfigure: boolean
  /** `storage.replace`, on a unit with equipment. */
  canReplaceUnit: boolean
  selectedIsUnit: boolean
}): { header: boolean; selected: boolean } {
  return {
    header: canOpenSettings({ canConfigure, canReplace: canReplaceUnit }),
    selected: canOpenSettings({ canConfigure, canReplace: canReplaceUnit && selectedIsUnit }),
  }
}

/** The modal's title: the unit says so, anything else goes by its code. */
export function settingsTitle(node: Pick<ApiStorageNode, 'code'>, isUnit: boolean): string {
  return isUnit ? `Unit settings · ${node.code}` : `Settings · ${node.code}`
}

/** What a kind is called in words people use: a position is a slot. */
export function kindName(kind: StorageNodeKind): string {
  if (kind === 'POSITION') return 'Slot'
  if (kind === 'ZONE') return 'Section'
  return STORAGE_NODE_KIND_LABELS[kind]
}

/**
 * One row of the Selected card's details. `condition` is set on the Kept-at
 * row (drawn as a badge, `value` empty); `note` says where an inherited value
 * comes from.
 */
export type DetailRow = {
  key: 'type' | 'keptAt' | 'zone' | 'layout' | 'places' | 'blocked' | 'position'
  label: string
  value: string
  condition?: StorageCondition | null
  note?: string
}

/**
 * The Selected card's details for the last node of `chain` (root→node):
 * Type, Kept at and Zone (each saying when it is inherited), then a
 * container's Layout, Places and Blocked, or a slot's row and column.
 *
 * Configuration only — the Equipment surface never shows what is stored, so
 * there is no used count here, ever.
 */
export function nodeDetails({
  chain,
  zones,
  capacity,
}: {
  chain: ApiStorageNode[]
  zones: { id: string; name: string }[]
  /** The node's own capacity, when the tree carries it. */
  capacity: StorageCapacity | null
}): DetailRow[] {
  const node = chain[chain.length - 1]
  if (!node) return []
  const zoneName = (id: string) => zones.find((z) => z.id === id)?.name ?? id

  const keptAt = resolveKeptAt(chain)
  const keptAtRow: DetailRow = { key: 'keptAt', label: 'Kept at', value: '', condition: keptAt.condition }
  if (keptAt.source === 'inherited' && keptAt.fromName) keptAtRow.note = `same as ${keptAt.fromName}`
  if (keptAt.source === 'legacy') keptAtRow.note = 'set directly'

  const fromAbove = inheritedZone(chain)
  const zoneRow: DetailRow = node.zoneId
    ? { key: 'zone', label: 'Zone', value: zoneName(node.zoneId) }
    : fromAbove?.zoneId
      ? { key: 'zone', label: 'Zone', value: zoneName(fromAbove.zoneId), note: `same as ${fromAbove.fromName}` }
      : { key: 'zone', label: 'Zone', value: 'None' }

  const rows: DetailRow[] = [{ key: 'type', label: 'Type', value: kindName(node.kind) }, keptAtRow, zoneRow]

  if (isOccupiable(node.kind)) {
    if (node.gridRow != null && node.gridColumn != null) {
      rows.push({ key: 'position', label: 'Position', value: `Row ${node.gridRow} · Column ${node.gridColumn}` })
    }
    return rows
  }

  if (node.gridRows && node.gridColumns) {
    rows.push({ key: 'layout', label: 'Layout', value: `${node.gridRows} × ${node.gridColumns}` })
    rows.push({ key: 'places', label: 'Places', value: String(capacity?.declared ?? node.gridRows * node.gridColumns) })
  } else if (capacity && capacity.declared > 0) {
    rows.push({ key: 'places', label: 'Places', value: String(capacity.declared) })
  }
  if (capacity) rows.push({ key: 'blocked', label: 'Blocked', value: String(capacity.blocked) })
  return rows
}

/**
 * The header's summary line, minus the status (rendered with its dot):
 * "40 of 40 spaces used", "2 blocked", "Zone Cell lab". A unit with nothing
 * declared says "Not set up" instead of a row of zeros. `layoutOnly`
 * (Equipment) states the layout — "40 spaces" — and not how much is used.
 */
export function unitSummaryParts(
  capacity: StorageCapacity | null,
  zoneName: string | null,
  layoutOnly = false,
): string[] {
  const parts: string[] = []
  if (capacity) {
    if (capacity.declared === 0) parts.push('Not set up')
    else
      parts.push(
        layoutOnly ? `${capacity.spaces} spaces` : `${capacity.used} of ${capacity.spaces} spaces used`,
        `${capacity.blocked} blocked`,
      )
  }
  if (zoneName) parts.push(`Zone ${zoneName}`)
  return parts
}

/** Whether a materialised path is the unit's own or lies beneath it. */
export function isInUnit(path: string, unitPath: string): boolean {
  return path === unitPath || path.startsWith(`${unitPath}/`)
}

/**
 * What clicking a slot on the Equipment map lets this user do — renaming is
 * `inventory.configure`, blocking `inventory.manage`. Null when neither, so
 * the map promises nothing it cannot deliver.
 */
export function equipmentSlotHint({ canName, canBlock }: { canName: boolean; canBlock: boolean }): string | null {
  if (canName && canBlock) return 'Click a slot to name it or block it.'
  if (canName) return 'Click a slot to name it.'
  if (canBlock) return 'Click a slot to block it or put it back in service.'
  return null
}

/** The unit a materialised path belongs to, matched by whole first segment. */
export function ownerUnitOf<T extends Pick<ApiStorageNode, 'path'>>(path: string, units: T[]): T | undefined {
  return units.find((u) => isInUnit(path, u.path))
}

/**
 * The container in hand for a selection (`holder`) and the grid the map draws
 * (`gridNode`).
 *
 * The tree holds containers only, so a selected slot is never in it; its box
 * is found by trimming the last segment off the slot's path. Falling back to
 * the box's parent grid keeps the map on screen when a slot is clicked, rather
 * than swapping it for the slot's detail. An id that cannot be placed yet
 * (still loading) shows the unit.
 */
export function mapNodeFor(
  nodes: ApiStorageNode[],
  unit: ApiStorageNode | null,
  selectedId: string | null,
  selectedPath: string | null,
): { holder: ApiStorageNode | null; gridNode: ApiStorageNode | null } {
  const hasGrid = (n: ApiStorageNode | undefined): boolean => Boolean(n?.gridRows && n.gridColumns)

  const inTree = selectedId ? nodes.find((n) => n.id === selectedId) : undefined
  const parentPath = selectedPath ? selectedPath.split('/').slice(0, -1).join('/') : null
  const byPath = parentPath ? nodes.find((n) => n.path === parentPath) : undefined
  const holder = inTree ?? byPath ?? unit
  if (!holder) return { holder: null, gridNode: null }

  if (hasGrid(holder)) return { holder, gridNode: holder }
  // A container with no grid of its own shows the declare form, not its
  // parent's map; only a slot falls back to the grid it sits in.
  if (!isOccupiable(holder.kind)) return { holder, gridNode: null }
  const parent = nodes.find((n) => n.id === holder.parentId)
  return { holder, gridNode: parent && hasGrid(parent) ? parent : null }
}
